import React, { createContext, useContext, useState } from 'react'
import { IModule } from 'src/components/Sidebar/Sidebar.Interface'

/**
 * Contexto para manejar los módulos seleccionados en la aplicación.
 * Proporciona las funcionalidades de agregar y eliminar módulos seleccionados.
 * 
 * @interface SelectedModulesContextType
 * @property {IModule[]} selectedModules - Lista de módulos seleccionados.
 * @property {function} addSelectedModule - Función para agregar un módulo a la lista de módulos seleccionados.
 * @property {function} removeSelectedModule - Función para eliminar un módulo de la lista de módulos seleccionados.
 */
interface SelectedModulesContextType {
  selectedModules: IModule[]
  addSelectedModule: (module: IModule) => void
  removeSelectedModule: (module: IModule) => void
}

/**
 * Contexto inicial para manejar los módulos seleccionados.
 * Contiene la lista de módulos seleccionados y funciones vacías por defecto.
 * 
 * @type {React.Context<SelectedModulesContextType>}
 */
const SelectedModulesContext = createContext<SelectedModulesContextType>({
  selectedModules: [],
  addSelectedModule: () => { },
  removeSelectedModule: () => { },
})

/**
 * Proveedor del contexto de módulos seleccionados.
 * 
 * @component
 * @param {React.ReactNode} children - Los componentes hijos que recibirán acceso al contexto.
 * @returns {JSX.Element} El proveedor del contexto que envuelve a los componentes hijos.
 * 
 * @example
 * ```tsx
 * <SelectedModulesProvider>
 *   <YourComponent />
 * </SelectedModulesProvider>
 * ```
 */
export const SelectedModulesProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [selectedModules, setSelectedModules] = useState<IModule[]>([])

  /**
   * Agrega un módulo a la lista de módulos seleccionados.
   * 
   * @param {IModule} module - El módulo que se desea agregar.
   * @example
   * ```ts
   * addSelectedModule(module)
   * ```
   */
  const addSelectedModule = (module: IModule) => {
    setSelectedModules([...selectedModules, module])
  }

  /**
   * Elimina un módulo de la lista de módulos seleccionados.
   * 
   * @param {IModule} moduleToRemove - El módulo que se desea eliminar.
   * @example
   * ```ts
   * removeSelectedModule(module)
   * ```
   */
  const removeSelectedModule = (moduleToRemove: IModule) => {
    setSelectedModules(
      selectedModules.filter((module) => module !== moduleToRemove),
    )
  }

  return (
    <SelectedModulesContext.Provider
      value={{ selectedModules, addSelectedModule, removeSelectedModule }}
    >
      {children}
    </SelectedModulesContext.Provider>
  )
}

/**
 * Hook personalizado para acceder al contexto de los módulos seleccionados.
 * 
 * @returns {SelectedModulesContextType} El contexto de módulos seleccionados.
 * 
 * @example
 * ```ts
 * const { selectedModules, addSelectedModule, removeSelectedModule } = useSelectedModules();
 * ```
 */
export const useSelectedModules = () => {
  return useContext(SelectedModulesContext)
}
